import React, { useState } from 'react';
import { Loader2, Users, UserPlus, Zap, Target, ChevronDown, ChevronUp, Sparkles, Wand2, AlertTriangle, Eye, FileText, X, Clipboard, ListChecks, GitCompare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { TargetingInsights } from '../TargetingInsights';
import { BatchComparison } from './BatchComparison';
import { CandidateCard } from './CandidateCard';
import { CandidateComparison } from './CandidateComparison';
import { PitchGenerator } from './PitchGenerator';
import { SubmissionFlow } from './SubmissionFlow';
import { generateSnapshot } from './utils';
import { CANDIDATE_STAGES, VERTICAL_COLORS, STRENGTH_DOT, STRENGTH_LABEL_MAP } from './constants';

export const RoleCard = ({
  role,
  roleIndex,
  isExpanded,
  onToggle,
  candidates,
  batchData,
  isFocusTop,
  focusRank,
  sourcingRole,
  onSource,
  smartResourcing,
  onSmartResource,
  snapshotPreview,
  onSnapshotPreviewToggle,
  onCandidateNoteAdded,
  onCandidateSubmissionToggled,
  onCandidateStageChanged,
}) => {
  const [compareSelection, setCompareSelection] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [showPitch, setShowPitch] = useState(false);
  const [showSubmitFlow, setShowSubmitFlow] = useState(false);
  const [pitchText, setPitchText] = useState('');
  const [pitchType, setPitchType] = useState('short');

  const pq = role.pipeline_quality || {};
  const viability = role.viability || {};
  const tierTotal = (pq.high_fit || 0) + (pq.medium_fit || 0) + (pq.low_fit || 0);
  const isWeak = pq.strength === 'weak' || pq.strength === 'weak_needs_refinement';
  const recommendedCandidates = candidates.filter(c => c.recommended);
  const otherCandidates = candidates.filter(c => !c.recommended);

  // Gather candidates for pitch: those in submission list, or top recommended
  const pitchCandidates = (() => {
    const submitted = candidates.filter(c => c.in_submission);
    if (submitted.length > 0) return submitted.slice(0, 3);
    return recommendedCandidates.slice(0, 3);
  })();

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all ${isFocusTop ? 'border-[#D4AF37] ring-1 ring-[#D4AF37]/30' : pq.strength === 'strong' ? 'border-emerald-200' : isWeak ? 'border-red-200' : 'border-gray-200'}`} data-testid={`role-${roleIndex}`}>
      {isFocusTop && (
        <div className="flex items-center gap-1.5 px-5 py-1 bg-[#D4AF37]/10 border-b border-[#D4AF37]/20" data-testid={`focus-label-${roleIndex}`}>
          <Zap className="h-3 w-3 text-[#D4AF37]" />
          <span className="text-[9px] font-bold text-[#8B7425] uppercase tracking-wide">Top Opportunity #{focusRank + 1}</span>
        </div>
      )}
      <div className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50/50"
        onClick={onToggle}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col items-center gap-1 flex-shrink-0" data-testid={`strength-${roleIndex}`}>
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center relative ${pq.strength === 'strong' ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-[#0A1F44]'}`}>
              <Users className={`h-4 w-4 ${pq.strength === 'strong' ? 'text-emerald-600' : 'text-[#D4AF37]'}`} />
              <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white ${STRENGTH_DOT[pq.strength] || 'bg-gray-300'}`} title={STRENGTH_LABEL_MAP[pq.strength] || ''}></span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#0A1F44] truncate">{role.title}</span>
              <span className={`px-2 py-0.5 text-[9px] font-semibold rounded border ${VERTICAL_COLORS[role.vertical] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                {role.vertical_label}
              </span>
              {pq.strength && pq.strength !== 'empty' && (
                <span className={`px-1.5 py-0.5 text-[8px] font-bold rounded ${pq.strength === 'strong' ? 'bg-emerald-100 text-emerald-700' : pq.strength === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-testid={`strength-badge-${roleIndex}`}>
                  {STRENGTH_LABEL_MAP[pq.strength]}
                </span>
              )}
              {role.last_targeting_label && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-[#D4AF37]/15 text-[#8B7425] border border-[#D4AF37]/30" data-testid={`targeting-label-${roleIndex}`}>
                  {role.last_targeting_label}
                </span>
              )}
              {viability.score > 0 && (
                <span className={`px-1.5 py-0.5 text-[7px] font-bold rounded ${viability.score >= 60 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : viability.score >= 30 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                  data-testid={`viability-score-${roleIndex}`} title={viability.reasoning?.join('; ')}>
                  {viability.viability_label || `Viability: ${viability.score}`}
                </span>
              )}
              {viability.excluded && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-red-100 text-red-700 border border-red-300" data-testid={`tag-excluded-${roleIndex}`}>Excluded Company</span>
              )}
              {viability.enterprise && !viability.excluded && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-orange-100 text-orange-700 border border-orange-300" data-testid={`tag-enterprise-${roleIndex}`}>Enterprise (Low Priority)</span>
              )}
              {viability.company_tier === 'mid_market' && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`tag-midmarket-${roleIndex}`}>Mid-Market (Preferred)</span>
              )}
              {viability.company_tier === 'size_unknown' && !viability.excluded && !viability.enterprise && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-gray-100 text-gray-500 border border-gray-200" data-testid={`tag-size-unknown-${roleIndex}`}>Size Unknown</span>
              )}
              {(viability.tags || []).includes('leadership') && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-orange-50 text-orange-600 border border-orange-200" data-testid={`tag-leadership-${roleIndex}`}>Leadership</span>
              )}
              {(viability.tags || []).includes('clearance_required') && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-red-50 text-red-600 border border-red-200" data-testid={`tag-clearance-${roleIndex}`}>Clearance Required</span>
              )}
              {(viability.tags || []).includes('niche') && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-gray-100 text-gray-600 border border-gray-200" data-testid={`tag-niche-${roleIndex}`}>Niche</span>
              )}
              {(viability.tags || []).includes('high_availability') && (
                <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-emerald-50 text-emerald-600 border border-emerald-200" data-testid={`tag-availability-${roleIndex}`}>High Avail.</span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
              <span>{role.company}</span>
              <span>{role.location}</span>
              <span className="capitalize">{role.criteria?.seniority || 'mid'} level</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          {tierTotal > 0 && (
            <div className="hidden md:flex flex-col items-end gap-0.5" data-testid={`tier-bar-${roleIndex}`}>
              <div className="flex items-center gap-0.5 h-2 w-24 bg-gray-100 rounded-full overflow-hidden">
                {pq.high_fit > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${(pq.high_fit / tierTotal) * 100}%` }}></div>}
                {pq.medium_fit > 0 && <div className="bg-amber-400 h-full" style={{ width: `${(pq.medium_fit / tierTotal) * 100}%` }}></div>}
                {pq.low_fit > 0 && <div className="bg-red-400 h-full" style={{ width: `${(pq.low_fit / tierTotal) * 100}%` }}></div>}
              </div>
              <span className="text-[8px] text-gray-400">Avg {pq.avg_score}</span>
            </div>
          )}
          <div className="text-center">
            <p className="text-sm font-bold text-[#0A1F44]">{role.candidates_count || 0}</p>
            <p className="text-[9px] text-gray-400">Candidates</p>
          </div>
          <button onClick={e => { e.stopPropagation(); onSource(role.role_id); }}
            disabled={sourcingRole === role.role_id}
            className="px-3 py-1.5 bg-[#D4AF37] text-[#0A1F44] text-[10px] font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1"
            data-testid={`source-btn-${roleIndex}`}>
            {sourcingRole === role.role_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
            Source
          </button>
          {isWeak && (role.candidates_count || 0) > 0 && (
            <button onClick={e => { e.stopPropagation(); onSmartResource(role.role_id); }}
              disabled={smartResourcing === role.role_id}
              className="px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-bold rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1"
              data-testid={`smart-resource-btn-${roleIndex}`}>
              {smartResourcing === role.role_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
              Smart Re-Source
            </button>
          )}
          {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30">
          {/* Quality strip */}
          {tierTotal > 0 && (
            <div className="flex items-center gap-4 mb-4 p-3 bg-white border border-gray-200 rounded-lg" data-testid={`quality-strip-${roleIndex}`}>
              <div className="flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5 text-[#0A1F44]" />
                <span className="text-[10px] font-semibold text-[#0A1F44]">Avg Score: {pq.avg_score}</span>
              </div>
              <div className="h-4 w-px bg-gray-200"></div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-[10px]"><span className="h-2 w-2 rounded-full bg-emerald-500 inline-block"></span> High Fit: <b className="text-emerald-700">{pq.high_fit}</b></span>
                <span className="flex items-center gap-1 text-[10px]"><span className="h-2 w-2 rounded-full bg-amber-400 inline-block"></span> Medium: <b className="text-amber-700">{pq.medium_fit}</b></span>
                <span className="flex items-center gap-1 text-[10px]"><span className="h-2 w-2 rounded-full bg-red-400 inline-block"></span> Low Fit: <b className="text-red-700">{pq.low_fit}</b></span>
              </div>
              <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                {pq.high_fit > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${(pq.high_fit / tierTotal) * 100}%` }}></div>}
                {pq.medium_fit > 0 && <div className="bg-amber-400 h-full" style={{ width: `${(pq.medium_fit / tierTotal) * 100}%` }}></div>}
                {pq.low_fit > 0 && <div className="bg-red-400 h-full" style={{ width: `${(pq.low_fit / tierTotal) * 100}%` }}></div>}
              </div>
            </div>
          )}

          {/* Stage Counts */}
          {candidates.length > 0 && (() => {
            const sc = {};
            candidates.forEach(c => { const s = c.stage || 'sourced'; sc[s] = (sc[s] || 0) + 1; });
            const hasNonSourced = Object.keys(sc).some(k => k !== 'sourced' && sc[k] > 0);
            return (
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid={`stage-counts-${roleIndex}`}>
                {CANDIDATE_STAGES.map(st => {
                  const count = sc[st.value] || 0;
                  if (count === 0 && !hasNonSourced && st.value !== 'sourced') return null;
                  if (count === 0) return null;
                  return (
                    <span key={st.value} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-bold ${st.cls}`}>
                      {count} {st.label}
                    </span>
                  );
                })}
              </div>
            );
          })()}

          {/* Snapshot & Pitch Actions */}
          {candidates.length > 0 && (
            <div className="flex items-center gap-2 mb-3" data-testid={`snapshot-actions-${roleIndex}`}>
              <button onClick={() => {
                const text = generateSnapshot({ ...role, pipeline_quality: pq }, candidates);
                navigator.clipboard.writeText(text);
                toast.success('Pipeline snapshot copied to clipboard');
              }}
                className="px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-bold rounded-lg hover:bg-[#0d2a5c] flex items-center gap-1.5"
                data-testid={`copy-snapshot-${roleIndex}`}>
                <FileText className="h-3 w-3" /> Copy Snapshot
              </button>
              <button onClick={() => onSnapshotPreviewToggle(role.role_id)}
                className={`px-3 py-1.5 text-[10px] font-bold rounded-lg flex items-center gap-1.5 transition-all ${snapshotPreview === role.role_id ? 'bg-[#0A1F44] text-white' : 'bg-white text-gray-600 border border-gray-300 hover:border-[#0A1F44]'}`}
                data-testid={`preview-snapshot-${roleIndex}`}>
                <Eye className="h-3 w-3" /> Preview
              </button>
              {pitchCandidates.length > 0 && (
                <button onClick={() => setShowPitch(true)}
                  className="px-3 py-1.5 bg-[#D4AF37] text-[#0A1F44] text-[10px] font-bold rounded-lg hover:bg-[#c5a030] flex items-center gap-1.5"
                  data-testid={`generate-pitch-${roleIndex}`}>
                  <Wand2 className="h-3 w-3" /> Generate Client Pitch ({pitchCandidates.length})
                </button>
              )}
              {pitchCandidates.length > 0 && pitchText && (
                <button onClick={() => setShowSubmitFlow(true)}
                  className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-lg hover:bg-emerald-700 flex items-center gap-1.5"
                  data-testid={`submit-to-client-${roleIndex}`}>
                  <Send className="h-3 w-3" /> Submit to Client
                </button>
              )}
            </div>
          )}

          {/* Snapshot Preview */}
          {snapshotPreview === role.role_id && candidates.length > 0 && (
            <div className="mb-4 p-4 bg-white border border-[#0A1F44]/20 rounded-lg font-mono" data-testid={`snapshot-preview-${roleIndex}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold font-sans">Snapshot Preview</span>
                <button onClick={() => onSnapshotPreviewToggle(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <pre className="text-[11px] text-[#0A1F44] whitespace-pre-wrap leading-relaxed overflow-auto max-h-[350px]">
                {generateSnapshot({ ...role, pipeline_quality: pq }, candidates)}
              </pre>
            </div>
          )}

          {/* Search criteria */}
          <div className="mb-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1">Search Criteria</p>
            <div className="flex flex-wrap gap-1">
              {(role.criteria?.title_keywords || []).slice(0, 8).map((kw, ki) => (
                <span key={ki} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-600">{kw}</span>
              ))}
            </div>
          </div>

          {/* Batch Comparison */}
          <BatchComparison batchData={batchData} roleIndex={roleIndex} />

          {/* Submission List */}
          {(() => {
            const submitted = candidates.filter(c => c.in_submission);
            if (submitted.length === 0) return null;
            return (
              <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`submission-list-${roleIndex}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-[10px] text-emerald-800 uppercase tracking-wider font-bold">Submission List ({submitted.length})</span>
                  </div>
                  <button onClick={() => {
                    const lines = submitted.map(c => `${c.name} | ${c.title} | ${c.current_company} | ${c.location || ''} | Score: ${c.fit_score}`);
                    navigator.clipboard.writeText(lines.join('\n'));
                    toast.success('Submission list copied');
                  }} className="px-2 py-1 text-[9px] font-medium text-emerald-700 hover:bg-emerald-100 rounded"
                    data-testid={`copy-submission-${roleIndex}`}>
                    <Clipboard className="h-3 w-3 inline mr-1" />Copy All
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {submitted.map((c, si) => (
                    <span key={si} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-emerald-200 rounded-md text-[10px] text-[#0A1F44] font-medium">
                      <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[8px] font-bold ${c.fit_score >= 50 ? 'bg-emerald-100 text-emerald-700' : c.fit_score >= 30 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{c.fit_score}</span>
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Targeting Insights */}
          <TargetingInsights roleId={role.role_id} />

          {/* Compare Candidates Button */}
          {candidates.length >= 2 && (
            <div className="flex items-center gap-2 mb-3" data-testid={`compare-controls-${roleIndex}`}>
              <button onClick={() => setCompareSelection(sel => sel.length > 0 ? [] : candidates.filter(c => c.recommended).slice(0, 3).map(c => ({
                  candidate_key: c.candidate_key, name: c.name, title: c.title,
                  current_company: c.current_company, fit_score: c.fit_score,
                }))
              )}
                className={`px-3 py-1.5 text-[10px] font-bold rounded-lg flex items-center gap-1.5 transition-all ${compareSelection.length > 0 ? 'bg-[#0A1F44] text-white' : 'bg-white text-gray-600 border border-gray-300 hover:border-[#0A1F44]'}`}
                data-testid={`toggle-compare-${roleIndex}`}>
                <GitCompare className="h-3 w-3" />
                {compareSelection.length > 0 ? `Compare ${compareSelection.length} Selected` : 'Select to Compare'}
              </button>
              {compareSelection.length >= 2 && (
                <button onClick={() => setShowComparison(true)}
                  className="px-3 py-1.5 bg-[#D4AF37] text-[#0A1F44] text-[10px] font-bold rounded-lg hover:bg-[#c5a030] flex items-center gap-1.5"
                  data-testid={`run-compare-${roleIndex}`}>
                  <Target className="h-3 w-3" /> Run AI Comparison
                </button>
              )}
              {compareSelection.length > 0 && compareSelection.length < 2 && (
                <span className="text-[10px] text-gray-400">Select at least 2 candidates (max 3)</span>
              )}
            </div>
          )}

          {/* Candidates */}
          {candidates.length === 0 ? (
            <p className="text-xs text-gray-400 py-3">No candidates sourced yet. Click "Source" to find matching candidates via Apollo.</p>
          ) : (
            <div className="space-y-3">
              {recommendedCandidates.length > 0 && (
                <div data-testid={`recommended-section-${roleIndex}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
                    <p className="text-[10px] text-[#0A1F44] uppercase tracking-wider font-bold">Recommended Candidates ({recommendedCandidates.length})</p>
                  </div>
                  <div className="space-y-1.5">
                    {recommendedCandidates.map((c, ci) => (
                      <CandidateCard key={`rec-${ci}`} candidate={c} recommended roleIndex={roleIndex} candidateIndex={ci}
                        onNoteAdded={onCandidateNoteAdded}
                        onSubmissionToggled={onCandidateSubmissionToggled}
                        onStageChanged={onCandidateStageChanged}
                        selectable={compareSelection.length > 0}
                        selected={compareSelection.some(s => s.candidate_key === c.candidate_key)}
                        onToggleSelect={(cand) => {
                          setCompareSelection(prev => {
                            const exists = prev.find(s => s.candidate_key === cand.candidate_key);
                            if (exists) return prev.filter(s => s.candidate_key !== cand.candidate_key);
                            if (prev.length >= 3) { toast.info('Max 3 candidates for comparison'); return prev; }
                            return [...prev, { candidate_key: cand.candidate_key, name: cand.name, title: cand.title, current_company: cand.current_company, fit_score: cand.fit_score }];
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              {otherCandidates.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">Other Candidates ({otherCandidates.length})</p>
                  <div className="space-y-1.5">
                    {otherCandidates.slice(0, 10).map((c, ci) => (
                      <CandidateCard key={`other-${ci}`} candidate={c} recommended={false} roleIndex={roleIndex} candidateIndex={ci}
                        onNoteAdded={onCandidateNoteAdded}
                        onSubmissionToggled={onCandidateSubmissionToggled}
                        onStageChanged={onCandidateStageChanged}
                        selectable={compareSelection.length > 0}
                        selected={compareSelection.some(s => s.candidate_key === c.candidate_key)}
                        onToggleSelect={(cand) => {
                          setCompareSelection(prev => {
                            const exists = prev.find(s => s.candidate_key === cand.candidate_key);
                            if (exists) return prev.filter(s => s.candidate_key !== cand.candidate_key);
                            if (prev.length >= 3) { toast.info('Max 3 candidates for comparison'); return prev; }
                            return [...prev, { candidate_key: cand.candidate_key, name: cand.name, title: cand.title, current_company: cand.current_company, fit_score: cand.fit_score }];
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Candidate Comparison Modal */}
          {showComparison && compareSelection.length >= 2 && (
            <CandidateComparison
              roleId={role.role_id}
              roleTitle={role.title}
              company={role.company}
              selectedCandidates={compareSelection}
              onClose={() => { setShowComparison(false); setCompareSelection([]); }}
              onCandidateUpdate={() => onCandidateNoteAdded()}
              onGeneratePitch={(candidateKeys, candidateNames) => {
                setShowComparison(false);
                setCompareSelection([]);
                setShowPitch(true);
              }}
            />
          )}

          {/* Pitch Generator Modal */}
          {showPitch && pitchCandidates.length > 0 && (
            <PitchGenerator
              roleId={role.role_id}
              roleTitle={role.title}
              company={role.company}
              candidateKeys={pitchCandidates.map(c => c.candidate_key)}
              candidateNames={pitchCandidates.map(c => c.name)}
              onClose={() => setShowPitch(false)}
              onPitchReady={(text, type) => {
                setPitchText(text);
                setPitchType(type);
                setShowPitch(false);
                setShowSubmitFlow(true);
              }}
            />
          )}

          {/* Submission Flow Modal */}
          {showSubmitFlow && (
            <SubmissionFlow
              roleId={role.role_id}
              roleTitle={role.title}
              company={role.company}
              location={role.location}
              candidateKeys={pitchCandidates.map(c => c.candidate_key)}
              candidateNames={pitchCandidates.map(c => c.name)}
              pitchText={pitchText}
              pitchType={pitchType}
              onClose={() => setShowSubmitFlow(false)}
              onSubmitted={() => {
                setShowSubmitFlow(false);
                setPitchText('');
                if (onCandidateNoteAdded) onCandidateNoteAdded();
                toast.success('Submission logged! View it in the Submissions tab.');
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
